import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin, Expand, Mail } from 'lucide-react';
import { SevenIslandsGallery } from '../components/SevenIslandsGallery';
import { ImageLightbox } from '../components/ImageLightbox';
import { sevenIslands } from '../data/sevenIslands';
const ease = [0.23, 1, 0.32, 1] as const;
export function SevenIslandsPage() {
  const [planOpen, setPlanOpen] = useState(false);
  return <div className="bg-white" data-id="element-517" data-mp="3r7vwczk8rxsm">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden bg-black" data-id="element-518" data-mp="3r749qe8mhaeh">
        <img src={sevenIslands.heroImage} alt="Aerial rendering of 7 Islands along the waterway in Cape Coral" className="absolute inset-0 w-full h-full object-cover" data-id="element-519" data-mp="3n72988emin0s" />
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" data-id="element-520" data-mp="3d7uzrwb0fciy" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16 md:pb-20" data-id="element-521" data-mp="38auo95ve5hkg">
          <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-[#C5A55A] transition-colors mb-10" data-id="element-522" data-mp="2o7yulfn8db42">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" data-id="element-523" data-mp="2ebcbavajys45" />
            All projects
          </Link>
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-524" data-mp="2jap7w7r87djx">
            <p className="text-[#C5A55A] text-xs md:text-sm tracking-[0.3em] uppercase mb-5" data-id="element-525" data-mp="3by1gfe0jxku8">
              Upcoming development · Coming {sevenIslands.opening}
            </p>
            <h1 className="font-serif font-bold text-white leading-[0.95]" data-id="element-526" data-mp="2x3et49yxkefz">
              <span className="block text-5xl md:text-7xl lg:text-8xl" data-id="element-527" data-mp="31yambdfwl7uo">
                {sevenIslands.name}
              </span>
              <span className="block text-2xl md:text-4xl italic font-normal text-[#C5A55A] mt-3" data-id="element-528" data-mp="1qkyqnpuarvjl">
                {sevenIslands.subtitle}
              </span>
            </h1>
            <p className="flex items-center gap-2 text-gray-200 mt-8" data-id="element-529" data-mp="3t6u9gf4lhhf7">
              <MapPin className="w-4 h-4 text-[#C5A55A] shrink-0" aria-hidden="true" data-id="element-530" data-mp="3o9viu6k53slr" />
              {sevenIslands.location}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 px-6 md:px-10" data-id="element-531" data-mp="3ei7wgk2fx1r5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20" data-id="element-532" data-mp="39jekiw2sxit3">
          <div className="lg:col-span-7" data-id="element-533" data-mp="35ib2q4171cp8">
            <img src={sevenIslands.logo} alt="7 Islands Resort & Marina Village logo" className="h-28 md:h-36 w-auto mb-10" data-id="element-534" data-mp="35h9sg1s04u8k" />
            <p className="text-2xl md:text-4xl font-serif text-black leading-snug mb-8" data-id="element-535" data-mp="2vkonthjw9l59">
              {sevenIslands.tagline}
            </p>
            <div className="w-16 h-[2px] bg-[#C5A55A] mb-8" data-id="element-536" data-mp="yqtr2go4dog5" />
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl" data-id="element-537" data-mp="ttjv0i686ejc">
              Seven connected islands on the water off Old Burnt Store Road —
              condominiums, apartments, custom homes with their own boat slips,
              a resort hotel and lagoon, and an island set aside entirely for
              the community.
            </p>
          </div>

          <div className="lg:col-span-5" data-id="element-538" data-mp="1g0ny9v4h6nn2">
            <dl className="grid grid-cols-2 border-t border-gray-200" data-id="element-539" data-mp="1g0e7i5blwcgb">
              {sevenIslands.facts.map(fact => <div key={fact.label} className="py-6 border-b border-gray-200 odd:pr-6" data-id="element-540" data-mp="if9vd7s9wulz">
                  <dt className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2" data-id="element-541" data-mp="sb3exb4etod9">
                    {fact.label}
                  </dt>
                  <dd className="text-2xl md:text-3xl font-serif font-bold text-black" data-id="element-542" data-mp="xbr4rtgwjz5w">
                    {fact.value}
                  </dd>
                </div>)}
            </dl>

            <h2 className="text-xs tracking-[0.25em] uppercase text-gray-500 mt-12 mb-4" data-id="element-543" data-mp="28nry7lopobrz">
              What's planned
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 border-t border-gray-200" data-id="element-544" data-mp="229mbjrcgl4g3">
              {sevenIslands.amenities.map(a => <li key={a} className="py-3 border-b border-gray-200 text-black text-sm" data-id="element-545" data-mp="2gap0qcyr2uv8">
                  {a}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <SevenIslandsGallery data-id="element-546" data-mp="2l8qkz17wx08a" />

      {/* Island by island + master plan */}
      <section className="py-24 px-6 md:px-10 bg-gray-50" data-id="element-547" data-mp="2yzlw6k5vvtf1">
        <div className="max-w-7xl mx-auto" data-id="element-548" data-mp="2yz3qkwxc02ug">
          <div className="max-w-3xl mb-14" data-id="element-549" data-mp="2u0inmpqp352b">
            <p className="text-[#C5A55A] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-550" data-mp="2u1ttcv3pamw2">
              The plan
            </p>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-black mb-6" data-id="element-551" data-mp="38y1b7ormzc86">
              Island by island
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed" data-id="element-552" data-mp="3nkro7neq2t9l">
              {sevenIslands.frontage}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-id="element-553" data-mp="3rl217k0xmjxy">
            <ol className="lg:col-span-5 border-t border-gray-300" data-id="element-554" data-mp="3dl95tbegnfjn">
              {sevenIslands.islands.map(island => <li key={island.number} className="flex gap-6 py-5 border-b border-gray-300" data-id="element-555" data-mp="3iju77e5g859o">
                  <span className="font-serif text-3xl text-[#C5A55A] w-8 shrink-0 leading-none pt-1" data-id="element-556" data-mp="38mzbt44h2kzm">
                    {island.number}
                  </span>
                  <div data-id="element-557" data-mp="2utq3z7w6a7j7">
                    <h3 className="font-serif text-xl font-bold text-black" data-id="element-558" data-mp="2zuxdpchvlgsi">
                      {island.name}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1" data-id="element-559" data-mp="2kyofme9q19ho">{island.detail}</p>
                  </div>
                </li>)}
            </ol>

            <div className="lg:col-span-7 lg:sticky lg:top-32" data-id="element-560" data-mp="26s4oaw9konsl">
              <button onClick={() => setPlanOpen(true)} className="group relative block w-full bg-white border border-gray-200 p-3 text-left" aria-label="Open the master concept plan full screen" data-id="element-561" data-mp="26tfq4qjgjlh2">
                <img src={sevenIslands.masterPlan.src} alt="7 Islands master concept plan" className="w-full h-auto" data-id="element-562" data-mp="1s402hjf83w5l" />
                <span className="absolute top-6 right-6 inline-flex items-center gap-2 bg-black text-white text-xs px-3 py-2 rounded-sm group-hover:bg-[#C5A55A] group-hover:text-black transition-colors duration-200" data-id="element-563" data-mp="2krcptvt7k7zy">
                  <Expand className="w-3.5 h-3.5" aria-hidden="true" data-id="element-564" data-mp="2asojhual5hc3" />
                  View full plan
                </span>
              </button>
              <p className="text-gray-500 text-sm mt-4" data-id="element-565" data-mp="14b9rpdj23iww">
                {sevenIslands.masterPlan.caption} Concept plan; not for
                construction.
              </p>
            </div>
          </div>

          <div className="mt-16 border-t border-gray-300 pt-10 max-w-3xl" data-id="element-566" data-mp="3g0zgqlmoan5c">
            <h3 className="font-serif text-2xl font-bold text-black mb-3" data-id="element-567" data-mp="3g19bsnmkrkhw">
              An island for the community
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-568" data-mp="3pqj1xxxuyt3d">
              {sevenIslands.communityIsland}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white py-24 px-6 md:px-10" data-id="element-569" data-mp="3us8c7bd9b2qe">
        <div className="max-w-5xl mx-auto text-center" data-id="element-570" data-mp="2kicbtzetaya">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-571" data-mp="7ko8nngmjlmp">
            Opening {sevenIslands.opening}.
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto" data-id="element-572" data-mp="ciy664ptmybh">
            For residences, hotel and commercial space, reach the 7 Islands team
            directly — or talk with Sinclair about the development.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-573" data-mp="2edgw9xxarnxz">
            <a href={sevenIslands.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#C5A55A] hover:bg-[#D4AF37] text-black px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-574" data-mp="k1p8e6a4mydu">
              Visit 7islandsFL.com
              <ArrowUpRight className="w-5 h-5" aria-hidden="true" data-id="element-575" data-mp="14eaoq458d0i" />
            </a>
            <a href={`mailto:${sevenIslands.email}`} className="inline-flex items-center gap-2 border border-white/40 hover:border-[#C5A55A] hover:text-[#C5A55A] text-white px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-576" data-mp="3sbh0pe88wd95">
              <Mail className="w-5 h-5" aria-hidden="true" data-id="element-577" data-mp="66df83bmdfz1" />
              {sevenIslands.email}
            </a>
          </div>
        </div>
      </section>

      <ImageLightbox images={[sevenIslands.masterPlan]} index={planOpen ? 0 : null} onClose={() => setPlanOpen(false)} onChange={() => undefined} data-id="element-578" data-mp="1hi4ia6uh1jew" />
    </div>;
}